<template>
	<div
		ref="tabs"
		:class=" [
			`tabs tabs--aligned-${alignment || tabAlignment}`,
			{
				'tabs--full-width' : fullWidth,
				'tabs--has-sm-inline-title' : inlineTitle && containerWidth < inlineTitle.breakpoint,
			}
		]"
		data-cy="tabs"
	>
		<div class="tabs__navigation">
			<ul
				ref="triggers"
				class="tabs__list"
			>
				<li
					v-for="(tab, index) in tabs"
					:key="index"
					:class="{ 'is-active': tab.isActive }"
					:data-cy="`tab-${index}`"
					class="tabs__list-item"
					@click="selectTab(tab)"
				>
					<span class="tabs__item-text">
						<helio-icon
							v-if="tab.icon"
							class="tabs__item-icon"
							:token="tab.icon"
							:weight="tab.iconWeight"
						/>

						<span class="tabs__item-title">{{ tab.name }}</span>

						<span
							v-if="tab.notificationCount"
							class="count"
						>
							{{ tab.notificationCount }}
						</span>
					</span>
				</li>
			</ul>

			<div
				ref="selectedLine"
				class="tabs__selected-line"
			/>

			<div
				v-if="inlineTitle && (tabAlignment !== 'center')"
				class="tabs__inline-title"
			>
				<helio-icon
					v-if="inlineTitle.iconToken"
					class="tabs__inline-title-icon"
					:token="inlineTitle.iconToken"
					:weight="inlineTitle.iconWeight ? inlineTitle.iconWeight : 'regular'"
				/>

				<span>{{ inlineTitle.title }}</span>
			</div>
		</div>

		<div class="tabs__details">
			<slot />
		</div>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';

export default {
	name: 'Tabs',

	components: {
		'helio-icon': Icon,
	},

	props: {
		fullWidth: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Tabs component will be the full width of its container',
		},

		inlineTitle: {
			type: Object,
			required: false,
			default: null,
			description: 'Sets an inline icon and/or title for left- or right-justified tabs. A custom breakpoint can decide when the title moves above the tabs.',
		},

		notificationCount: {
			type: Number,
			required: false,
			default: null,
			validator: notificationCount => notificationCount >= 0,
			description: 'Sets whether a notification badge will be displayed as well as the number shown in the notification',
		},

		tabAlignment: {
			type: String,
			required: false,
			default: 'center',
			validator: value => [
				'center',
				'flush-left',
				'flush-right',
				'left',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the alignment of the tabs',
		},
	},

	slots: {
		default: {
			required: true,
			scoped: false,
			description: 'Supplies content to the Tabs component',
		},
	},

	data() {
		return {
			alignment: null,
			children: null,
			containerWidth: 1000,
			tabs: [],
		};
	},

	watch: {
		'children'(newChildren) {
			this.tabs = newChildren.filter(child => child.$options.name === 'Tab');
			this.setTabPosition();
		},
	},

	created() {
		this.children = this.$children;
	},

	mounted() {
		window.addEventListener('resize', this.onResize);
		setTimeout(() => {
			this.onResize();
		}, 200);
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.getContainerWidth();
			this.setTabPosition(this.tabAlignment);
			this.setInlineTitlePosition();
		},

		selectTab(selectedTab) {
			if (this.tabs) {
				this.tabs.forEach((tab) => {
					tab.isActive = false;
				});
			}

			this.$emit('changed', selectedTab);
			selectedTab.isActive = true;
			this.onResize();
			this.$forceUpdate();
		},

		getContainerWidth() {
			const { tabs } = this.$refs;
			if (tabs) {
				this.containerWidth = tabs.clientWidth;
			}
		},

		setTabPosition(alignment) {
			setTimeout(() => {
				if (this.$refs && this.$refs.triggers) {
					const { selectedLine } = this.$refs;
					const selectedTab = this.$refs.triggers.querySelector('.is-active');
					const selectedWidth = selectedTab.clientWidth;
					const triggerTextWidth = selectedTab.querySelector('.tabs__item-text').clientWidth;
					let offsetX = selectedTab.offsetLeft + (selectedWidth / 2) - (triggerTextWidth / 2);
					// adjust offset of selected tab line if tabs are flush-left or flush-right
					if (alignment === 'flush-right') {
						offsetX = selectedTab.offsetLeft + (selectedWidth) - (triggerTextWidth);
					}
					if (alignment === 'flush-left') {
						offsetX = selectedTab.offsetLeft;
					}

					selectedLine.style.transform = `translate3d(${offsetX}px, 0px, 0px)`;
					selectedLine.style.width = `${triggerTextWidth}px`;
				}
			}, 100);
		},

		setInlineTitlePosition() {
			if (this.inlineTitle) {
				// if the container becomes too small to accomodate
				// the title inline with the tabs,
				// the title will appear above the tabs
				if (this.containerWidth >= this.inlineTitle.breakpoint) {
					this.alignment = this.tabsAlignment;
				} else {
					this.setTabPosition('flush-left');
					this.alignment = 'flush-left';
				}
			}
		},
	},
};
</script>

<style lang="scss" scoped src="./tabs.scss" />
